import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Copy,
  FileUp,
  Inbox,
  Loader2,
  Mail,
  PencilLine,
  Plus,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  ClaimWindowCountdown,
  RadianStateChip,
} from "@/components/radian/RadianStateChip";
import { radianErrorMessage, shortUuid } from "@/components/radian/format";
import {
  useImportReceivedDocument,
  useReceivedDocuments,
} from "@/lib/api/received-documents.queries";
import { useCompany } from "@/lib/api/companies.queries";
import { useActiveCompany } from "@/lib/company-context";
import type {
  RadianState,
  ReceivedDocumentSource,
} from "@/lib/api/received-documents";

export const Route = createFileRoute("/companies/$companyId/recibidas/")({
  component: ReceivedDocumentsPage,
});

const STATE_FILTERS: Array<RadianState | "all"> = [
  "all",
  "received",
  "acknowledged",
  "goods_received",
  "accepted",
  "claimed",
  "tacitly_accepted",
];

const SOURCE_ICON: Record<ReceivedDocumentSource, typeof Mail> = {
  email: Mail,
  manual_cufe: PencilLine,
  import: FileUp,
};

function ReceivedDocumentsPage() {
  return (
    <RequireAuth>
      <AppShell>
        <ReceivedDocumentsContent />
      </AppShell>
    </RequireAuth>
  );
}

function ReceivedDocumentsContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [q, setQ] = useState("");
  const [state, setState] = useState<RadianState | "all">("all");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [cufe, setCufe] = useState("");

  const filters = useMemo(
    () => ({
      ...(q.trim() ? { q: q.trim() } : {}),
      ...(state !== "all" ? { state } : {}),
      ...(pendingOnly ? { pendingAction: true } : {}),
      ...(periodFrom ? { periodFrom } : {}),
      ...(periodTo ? { periodTo } : {}),
    }),
    [q, state, pendingOnly, periodFrom, periodTo],
  );

  const listQuery = useReceivedDocuments(companyId, filters);
  const companyQuery = useCompany(companyId);
  const importMutation = useImportReceivedDocument(companyId);

  const documents = listQuery.data?.documents ?? [];
  const inboxEmail = companyQuery.data?.inboxEmail;
  const hasFilters =
    q.trim().length > 0 || state !== "all" || pendingOnly || !!periodFrom || !!periodTo;

  async function onCopyInbox() {
    if (!inboxEmail) return;
    try {
      await navigator.clipboard.writeText(inboxEmail);
      toast.success(t("recibidas.toast.inbox_copied"));
    } catch {
      toast.error(t("recibidas.toast.copy_failed"));
    }
  }

  async function onImport() {
    const trimmed = cufe.trim();
    if (trimmed.length < 40) {
      toast.error(t("recibidas.import.invalid_cufe"));
      return;
    }
    try {
      const result = await importMutation.mutateAsync({ cufe: trimmed });
      if (result.outcome === "imported") {
        toast.success(
          t("recibidas.toast.imported", { folio: result.document.documentId }),
        );
      } else {
        toast.info(
          t("recibidas.toast.duplicate", { folio: result.document.documentId }),
        );
      }
      setImportOpen(false);
      setCufe("");
    } catch (err) {
      toast.error(radianErrorMessage(err, t));
    }
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("recibidas.list.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("recibidas.list.subtitle")}
          </p>
          {inboxEmail ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="rounded-md bg-muted px-2 py-1 font-mono text-xs">
                {inboxEmail}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={() => void onCopyInbox()}
              >
                <Copy className="h-3.5 w-3.5" />
                {t("recibidas.list.copy_inbox")}
              </Button>
              <span className="text-xs text-muted-foreground">
                {t("recibidas.list.inbox_hint")}
              </span>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to="/companies/$companyId/recibidas/buzon" params={{ companyId }}>
              <Inbox className="h-4 w-4" />
              {t("recibidas.list.inbox_link")}
            </Link>
          </Button>
          <Button onClick={() => setImportOpen(true)}>
            <Plus className="h-4 w-4" />
            {t("recibidas.list.add_by_cufe")}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="grid gap-3 pt-6 md:grid-cols-5">
          <div>
            <Label htmlFor="recibidas-q">{t("recibidas.list.filter_q")}</Label>
            <Input
              id="recibidas-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("recibidas.list.filter_q_placeholder")}
            />
          </div>
          <div>
            <Label htmlFor="recibidas-state">
              {t("recibidas.list.filter_state")}
            </Label>
            <Select
              value={state}
              onValueChange={(v) => setState(v as RadianState | "all")}
            >
              <SelectTrigger id="recibidas-state">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATE_FILTERS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s === "all"
                      ? t("recibidas.stateFilter.all")
                      : t(`recibidas.state.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="recibidas-from">{t("recibidas.list.filter_from")}</Label>
            <Input
              id="recibidas-from"
              type="date"
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="recibidas-to">{t("recibidas.list.filter_to")}</Label>
            <Input
              id="recibidas-to"
              type="date"
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 self-end rounded-md border p-2.5 text-sm">
            <input
              type="checkbox"
              checked={pendingOnly}
              onChange={(e) => setPendingOnly(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            {t("recibidas.list.filter_pending")}
          </label>
        </CardContent>
      </Card>

      {listQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : listQuery.isError ? (
        <ErrorState
          title={t("recibidas.list.error_title")}
          description={t("recibidas.list.error_body")}
          error={listQuery.error}
          onRetry={() => void listQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : documents.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Inbox className="h-8 w-8 text-primary" />
            </div>
            <div className="max-w-sm space-y-1">
              <h2 className="text-lg font-semibold">
                {hasFilters
                  ? t("recibidas.list.empty_search_title")
                  : t("recibidas.list.empty_title")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {hasFilters
                  ? t("recibidas.list.empty_search_body")
                  : t("recibidas.list.empty_body")}
              </p>
            </div>
            {!hasFilters ? (
              <Button onClick={() => setImportOpen(true)}>
                <Plus className="h-4 w-4" />
                {t("recibidas.list.add_by_cufe")}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("recibidas.table.supplier")}</TableHead>
                <TableHead>{t("recibidas.table.folio")}</TableHead>
                <TableHead>{t("recibidas.table.issue_date")}</TableHead>
                <TableHead>{t("recibidas.table.due_date")}</TableHead>
                <TableHead className="text-right">
                  {t("recibidas.table.total")}
                </TableHead>
                <TableHead>{t("recibidas.table.dian")}</TableHead>
                <TableHead>{t("recibidas.table.state")}</TableHead>
                <TableHead>{t("recibidas.table.source")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((doc) => {
                const SourceIcon = SOURCE_ICON[doc.source];
                return (
                  <TableRow
                    key={doc.id}
                    className="cursor-pointer"
                    onClick={() =>
                      void navigate({
                        to: "/companies/$companyId/recibidas/$receivedId",
                        params: { companyId, receivedId: doc.id },
                      })
                    }
                  >
                    <TableCell>
                      <div className="max-w-[220px]">
                        <p className="truncate text-sm">{doc.supplier.name}</p>
                        <p className="font-mono text-xs text-muted-foreground">
                          {doc.supplier.nit}
                          {doc.supplier.dv ? `-${doc.supplier.dv}` : ""}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="font-mono text-sm font-medium text-primary">
                        {doc.documentId}
                      </p>
                      <p
                        className="font-mono text-[10px] text-muted-foreground"
                        title={doc.cufe}
                      >
                        {shortUuid(doc.cufe)}
                      </p>
                    </TableCell>
                    <TableCell className="text-sm">{doc.issueDate}</TableCell>
                    <TableCell className="text-sm">
                      {doc.dueDate ?? "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCop(doc.totalAmount)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={doc.dianValidated ? "success" : "warning"}>
                        {doc.dianValidated
                          ? t("recibidas.dian.valid")
                          : t("recibidas.dian.not_validated")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <RadianStateChip state={doc.radianState} />
                        <ClaimWindowCountdown
                          state={doc.radianState}
                          claimWindowEndsAt={doc.claimWindowEndsAt}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground"
                        title={t(`recibidas.source.${doc.source}`)}
                      >
                        <SourceIcon className="h-3.5 w-3.5" />
                        {t(`recibidas.source.${doc.source}`)}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Agregar por CUFE */}
      <Dialog
        open={importOpen}
        onOpenChange={(open) => {
          setImportOpen(open);
          if (!open) setCufe("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("recibidas.import.title")}</DialogTitle>
            <DialogDescription>{t("recibidas.import.body")}</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="recibidas-cufe">{t("recibidas.import.cufe_label")}</Label>
            <Input
              id="recibidas-cufe"
              value={cufe}
              onChange={(e) => setCufe(e.target.value)}
              placeholder={t("recibidas.import.cufe_placeholder")}
              className="font-mono"
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setImportOpen(false)}
              disabled={importMutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => void onImport()}
              disabled={importMutation.isPending || cufe.trim().length < 40}
            >
              {importMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {t("recibidas.import.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
